import React from 'react';
import { Button } from '../components/ui/button';
import { Phone, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const GinecoEstetica = () => {
  const lang = useLanguage();
  const t = getPageTexts('ginecoEstetica', lang);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#B8975A] to-[#8b7355]"></div>
        
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <a href="tel:952764547" className="text-[#8b7355] font-semibold hover:underline">
              952 76 45 47
            </a>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1C1C1A] text-center mb-6 leading-tight">
            {t.title}
          </h1>
          
          <p className="text-lg md:text-xl text-text-secondary text-center mb-8 max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: t.subtitle }} />
          
          <div className="text-center">
            <Button 
              onClick={() => scrollToSection('contacto')}
              className="bg-[#B8975A] hover:bg-[#a67259] text-white px-8 py-6 text-lg rounded transition-all duration-300"
              data-testid="gineco-request-consult-btn"
            >
              {t.requestConsult}
            </Button>
          </div>
        </div>
      </section>

      {/* Doctor Section */}
      <section className="py-16 bg-cream px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="lg:w-1/3">
              <img 
                src="/images/dra-susana-varela.jpg"
                alt={t.doctorName}
                className="w-full h-auto rounded-none shadow-lg"
              />
            </div>
            
            <div className="lg:w-2/3">
              <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6">
                {t.doctorName}
              </h2>
              
              {t.credentials && (
                <ul className="space-y-2">
                  {t.credentials.map((credential, index) => (
                    <li key={index} className="flex items-start gap-2 text-text-secondary">
                      <Check className="text-[#B8975A] mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-sm">{credential}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Consultas */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8 text-center">
            {t.consultTitle}
          </h2>
          
          <div className="space-y-4">
            {t.consults && t.consults.map((consulta, index) => (
              <div key={index} className="bg-ivory rounded-none p-6 shadow-sm border-l-4 border-[#B8975A]">
                <h3 className="text-lg font-semibold text-[#1C1C1A] flex items-center gap-2">
                  <Check className="text-[#B8975A]" size={20} />
                  {consulta}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tratamientos Regenerativos */}
      <section className="py-16 bg-cream px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8 text-center">
            {t.regenerativeTitle}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {t.regenerative && t.regenerative.map((item, index) => (
              <div key={index} className="bg-ivory rounded-none p-5 shadow-sm">
                <h3 className="text-base font-semibold text-[#1C1C1A] flex items-start gap-2">
                  <Check className="text-[#B8975A] mt-0.5 flex-shrink-0" size={18} />
                  <span>{item.name}</span>
                </h3>
                {item.desc && <p className="text-text-secondary text-sm mt-2 ml-7">{item.desc}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cirugía Ginecoestética */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8 text-center">
            {t.surgeryTitle}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {t.surgery && t.surgery.map((item, index) => (
              <div key={index} className="bg-cream rounded-none p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-[#1C1C1A] flex items-center gap-2">
                  <Check className="text-[#B8975A]" size={20} />
                  {item}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-[#B8975A] px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            {t.ctaTitle}
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            {t.ctaDesc}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:952764547"
              className="inline-flex items-center justify-center gap-2 bg-ivory text-[#B8975A] px-8 py-4 rounded-none font-semibold hover:bg-gray-100 transition-colors"
              data-testid="gineco-phone-btn"
            >
              <Phone size={20} />
              952 76 45 47
            </a>
            <a 
              href="https://wa.me/34696345792"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-gold500 text-white px-8 py-4 rounded-none font-semibold hover:bg-gold600 transition-colors"
              data-testid="gineco-whatsapp-btn"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default GinecoEstetica;
